import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Modal as ModalRoot,
  useFlashMessage,
  Select,
  Input,
  DatePicker,
  SelectChangeEvent,
  CollapsibleContentCard,
  Textarea,
  Checkbox,
  LargeTitle,
  Button,
  EllipsisLoader,
  Tooltip,
  palettes,
  SmallTitle
} from '@indigo-ag/component-module';
import i18next from 'core/i18n';
import {
  AgMatriculaDetailType,
  AgFinancingEvaluationType,
  AgFinancingMatriculaInput,
  MatriculaBiome,
  FinancingMunicipality,
  FinancingState,
  AgFinancingMatriculaDetail
} from '@indigo-ag/schema';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';

import { biomeTypesOptions, evaluationTypesOptions, GridContainer } from 'shared';
import {
  financingMunicipalitiesByState,
  financingStates,
  getAgFinancingMatriculas,
  updateAgFinancingMatricula,
  getSocioEnvironmentalData
} from 'features/brazil-grower/queries';
import { FormattedMatriculasDetailData, MatriculaContext } from '../MatriculasList';
import styled from 'styled-components';
import { MaterialUiPickersDate } from 'material-ui-pickers';
import { DeforestationMonitoringWrapper } from './DeforestationMonitoringWrapper';
import { date } from 'shared/utils';

const StyledCollapsibleContentCard = styled(CollapsibleContentCard)`
  margin-bottom: 16px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
`;

const Modal = styled(ModalRoot)`
  width: 100%;
  max-width: 980px;
`;

const ActionsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const LoadingWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  padding: 16px;
`;

type FormState = Partial<AgFinancingMatriculaDetail> | null;

interface UpdateDetailModalProps {
  closeModal: () => void;
  open: boolean;
  selectedMatricula?: FormattedMatriculasDetailData;
}

export const UpdateDetailModal: React.FunctionComponent<UpdateDetailModalProps> = ({
  closeModal,
  open,
  selectedMatricula
}) => {
  /**
   * MatriculaContext
   */
  const { agFinancingId } = useContext(MatriculaContext);

  /**
   * State
   */
  const [state, setState] = useState<FormState>(null);
  const [selectedState, setSelectedState] = useState(selectedMatricula?.detail?.state?.id ?? '0');
  const [selectedMunicipality, setSelectedMunicipality] = useState(selectedMatricula?.detail?.municipality?.id ?? '0');
  const [selectedBiome, setSelectedBiome] = useState<MatriculaBiome | null>(null);
  const isAddDisabled = !state?.registrationNumbers || !state?.totalArea || !state?.cultivableArea || !selectedBiome;
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Queries
   */
  const { data: statesData } = useQuery<{ financingStates: FinancingState[] }>(financingStates);
  const { data: municipalitiesData } = useQuery<{ financingMunicipalities: FinancingMunicipality[] }>(
    financingMunicipalitiesByState,
    {
      variables: { stateId: Number(selectedState) }
    }
  );

  const [getData, { data: formData, loading }] = useLazyQuery<{ socioEnvironmentalData: AgFinancingMatriculaDetail }>(
    getSocioEnvironmentalData,
    {
      fetchPolicy: 'network-only'
    }
  );

  const getSocioEnvironmentalReport = useCallback(
    (forceVegaReport = false): void => {
      if (selectedMatricula?.id) {
        getData({ variables: { matriculaNumber: selectedMatricula?.id, forceVegaReport } });
      }
    },
    [selectedMatricula, getData]
  );

  useEffect(() => {
    if (open) {
      getSocioEnvironmentalReport();
    }
  }, [open, getSocioEnvironmentalReport]);

  useEffect(() => {
    setState(formData?.socioEnvironmentalData ? { ...formData.socioEnvironmentalData } : null);
    setSelectedBiome(formData?.socioEnvironmentalData?.biome ?? null);
  }, [formData, open]);

  /**
   * Mutations
   */
  const [updateAgFinancingMatriculaFn] = useMutation(updateAgFinancingMatricula);

  useEffect(() => {
    setSelectedState(selectedMatricula?.detail?.state?.id ?? '0');
    setSelectedMunicipality(selectedMatricula?.detail?.municipality?.id ?? '0');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | SelectChangeEvent<AgFinancingEvaluationType>
      | React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  const handleDateChange = (date: MaterialUiPickersDate): void => {
    setState({ ...state, emissionDate: date._d });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.name === 'preservationAreaNA') {
      if (state?.preservationArea === undefined || state?.preservationArea === null) {
        setState({ ...state, preservationArea: '' });
      } else {
        setState({ ...state, preservationArea: undefined });
      }
    } else if (event.target.name === 'complianceConflictAreaNA') {
      if (state?.complianceConflictArea === undefined || state?.complianceConflictArea === null) {
        setState({ ...state, complianceConflictArea: '' });
      } else {
        setState({ ...state, complianceConflictArea: undefined });
      }
    }
  };

  const handleBiomeChange = (event: SelectChangeEvent<MatriculaBiome>): void => {
    setSelectedBiome(event.target.value);
  };

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () => {
    if (!state) return;

    const matricula: AgFinancingMatriculaInput = {
      id: selectedMatricula?.id,
      detail: {
        cultivableArea: Number(state.cultivableArea),
        state: Number(selectedState) > 0 ? { id: Number(selectedState) } : undefined,
        municipality: Number(selectedMunicipality) > 0 ? { id: Number(selectedMunicipality) } : undefined,
        detailType: AgMatriculaDetailType.FIELD_MONITORING,
        biome: selectedBiome as MatriculaBiome,
        preservationArea: state.preservationArea ?? undefined,
        complianceConflictArea: state.complianceConflictArea,
        // TODO: add prodes and deter when convenient
        // prodes: state.prodes ?? undefined,
        // deter: state.deter ?? undefined,
        ibamaEmbargo: state.ibamaEmbargo,
        ICMBIOEmbargo: state.ICMBIOEmbargo,
        indigenousLand: state.indigenousLand,
        quilimbolasLand: state.quilimbolasLand,
        stateEmbargo: state.stateEmbargo,
        permanentPreservationArea: state.permanentPreservationArea,
        deforestationAfter2008: state.deforestationAfter2008,
        registrationNumbers: state.registrationNumbers,
        totalArea: state.totalArea,
        emissionDate: state.emissionDate,
        conservationUnities: state.conservationUnities,
        legalComments: state.legalComments
      }
    };
    setIsSaving(true);
    return updateAgFinancingMatriculaFn({
      refetchQueries: [{ query: getAgFinancingMatriculas, variables: { agFinancingId } }],
      variables: {
        agFinancingId,
        matricula
      }
    })
      .then(() => {
        addFlashMessage({
          content: `${i18next.t('matriculas')} ${i18next.t('hasBeenUpdatedSuccessfully')}`,
          title: '',
          variant: 'success'
        });
      })
      .finally(() => setIsSaving(false));
  };

  const modalBody = (
    <ModalContent>
      {loading && (
        <LoadingWrapper>
          <EllipsisLoader fontSize={30} color={palettes.brand.blue.light} />
        </LoadingWrapper>
      )}
      {state?.vegaReportDate && (
        <SmallTitle style={{ color: palettes.brand.teal.darker, marginBottom: '1rem' }}>
          {i18next.t('vegaReportDate', { value: date.formatDate(state.vegaReportDate) })}
        </SmallTitle>
      )}
      <StyledCollapsibleContentCard titleText={i18next.t('dataProperty')}>
        <GridContainer columns={2}>
          <DatePicker
            name="emissionDate"
            label={i18next.t('emissionDate')}
            value={state?.emissionDate || null}
            sizeVariant="slim"
            onChange={handleDateChange}
          />
          <Input
            label={`${i18next.t('registrationNumber')}*`}
            name="registrationNumbers"
            onChange={handleChange}
            value={state?.registrationNumbers ?? ''}
            sizeVariant="slim"
          />
          <Select
            label={i18next.t('state')}
            value={selectedState}
            name="stateId"
            options={statesData ? statesData.financingStates.map(s => ({ name: s.name, value: s.id })) : []}
            sizeVariant="slim"
            disabled
          />
          <Select
            label={i18next.t('municipality')}
            value={selectedMunicipality}
            name="municipalityId"
            options={
              Number(selectedState) > 0 && municipalitiesData
                ? municipalitiesData.financingMunicipalities.map(m => ({
                    name: m.name,
                    value: m.id
                  }))
                : []
            }
            disabled
            sizeVariant="slim"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>

      <StyledCollapsibleContentCard titleText={i18next.t('deforestationMonitoringData')}>
        <DeforestationMonitoringWrapper prodes={state?.prodes} deter={state?.deter} />
      </StyledCollapsibleContentCard>

      <StyledCollapsibleContentCard titleText={i18next.t('area')}>
        <GridContainer columns={2}>
          <Input
            label={`${i18next.t('totalArea')}*`}
            name="totalArea"
            onChange={handleChange}
            value={!state?.totalArea || isNaN(Number(state?.totalArea)) ? '' : String(state?.totalArea)}
            sizeVariant="slim"
          />
          <Input
            label={`${i18next.t('cultivableArea')}*`}
            name="cultivableArea"
            onChange={handleChange}
            value={!state?.cultivableArea || isNaN(Number(state?.cultivableArea)) ? '' : String(state?.cultivableArea)}
            sizeVariant="slim"
          />

          <Select
            label={`${i18next.t('biome')}*`}
            name="biome"
            options={biomeTypesOptions}
            value={selectedBiome}
            onChange={handleBiomeChange}
            sizeVariant="slim"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>

      <StyledCollapsibleContentCard titleText={i18next.t('restrictions')}>
        <GridContainer columns={2}>
          <div>
            <Input
              label={`${i18next.t('preservationArea')}*`}
              name="preservationArea"
              onChange={handleChange}
              value={
                !state?.preservationArea || isNaN(Number(state?.preservationArea))
                  ? ''
                  : String(state?.preservationArea)
              }
              sizeVariant="slim"
            />
            <Checkbox
              name="preservationAreaNA"
              checked={state?.preservationArea === null || state?.preservationArea === undefined}
              label={'N/A'}
              onChange={handleCheckboxChange}
              margin="dense"
            />
          </div>

          <div>
            <Input
              label={i18next.t('complianceConflictArea')}
              name="complianceConflictArea"
              onChange={handleChange}
              value={
                !state?.complianceConflictArea || isNaN(Number(state?.complianceConflictArea))
                  ? ''
                  : String(state?.complianceConflictArea)
              }
              sizeVariant="slim"
            />
            <Checkbox
              name="complianceConflictAreaNA"
              checked={state?.complianceConflictArea === null || state?.complianceConflictArea === undefined}
              label={'N/A'}
              onChange={handleCheckboxChange}
              margin="dense"
            />
          </div>

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('ibamaEmbargo')}
            name="ibamaEmbargo"
            options={evaluationTypesOptions}
            value={state?.ibamaEmbargo ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('conservationUnities')}
            name="conservationUnities"
            options={evaluationTypesOptions}
            value={state?.conservationUnities ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('indigenousLand')}
            name="indigenousLand"
            options={evaluationTypesOptions}
            value={state?.indigenousLand ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('quilimbolasLand')}
            name="quilimbolasLand"
            options={evaluationTypesOptions}
            value={state?.quilimbolasLand ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('ICMBIOEmbargo')}
            name="ICMBIOEmbargo"
            options={evaluationTypesOptions}
            value={state?.ICMBIOEmbargo ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />
          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('stateEmbargo')}
            name="stateEmbargo"
            options={evaluationTypesOptions}
            value={state?.stateEmbargo ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('permanentPreservationArea')}
            name="permanentPreservationArea"
            options={evaluationTypesOptions}
            value={state?.permanentPreservationArea ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />

          <Select
            placeholder={i18next.t('na')}
            label={i18next.t('deforestationAfter2008')}
            name="deforestationAfter2008"
            options={evaluationTypesOptions}
            value={state?.deforestationAfter2008 ?? null}
            onChange={handleChange}
            sizeVariant="slim"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>

      <StyledCollapsibleContentCard titleText={i18next.t('evaluation')}>
        <GridContainer columns={1}>
          <Textarea
            label={i18next.t('legalComments')}
            name="legalComments"
            onChange={handleChange}
            value={state?.legalComments ?? ''}
            sizeVariant="regular"
          />
        </GridContainer>
      </StyledCollapsibleContentCard>
    </ModalContent>
  );

  const modalActions = (
    <ActionsContainer>
      <Tooltip title={i18next.t('socioEnvironmentalButtonHint')}>
        <Button onClick={() => getSocioEnvironmentalReport(true)} disabled={loading} variant="tertiary" busy={loading}>
          {i18next.t('socioEnvironmentalButtonLabel')}
        </Button>
      </Tooltip>
      <ActionsWrapper>
        <Button onClick={closeModal} variant="secondary">
          {i18next.t('cancel')}
        </Button>
        <Button onClick={updateInfo} disabled={isAddDisabled || isSaving} variant="primary" busy={isSaving}>
          {i18next.t('saveChanges')}
        </Button>
      </ActionsWrapper>
    </ActionsContainer>
  );

  return (
    <Modal
      open={open}
      title={selectedMatricula?.matricula?.documentName ?? ''}
      onRequestClose={closeModal}
      renderContent={() => modalBody}
      renderHeader={({ CloseButton }) => (
        <HeaderContainer>
          <LargeTitle>{selectedMatricula?.matricula?.documentName}</LargeTitle>
          <CloseButton />
        </HeaderContainer>
      )}
      renderActionArea={() => modalActions}
    />
  );
};
